import React from 'react';
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  Ellipsis,
  List,
  Map,
  Minus,
  Pencil,
  Plus,
  Thermometer,
  Trash2,
  X,
} from 'lucide-react';

const CONTROL_ICON_SIZE = 15;
const BUTTON_ICON_SIZE = 18;
const BUTTON_ICON_STROKE = 2.2;
const FIELD_ICON_STROKE = 1.9;

export const ListIcon = (): JSX.Element => <List size={CONTROL_ICON_SIZE} aria-hidden />;

export const FloorplanIcon = (): JSX.Element => <Map size={CONTROL_ICON_SIZE} aria-hidden />;

export const ThermometerIcon = (): JSX.Element => (
  <Thermometer size={CONTROL_ICON_SIZE} strokeWidth={FIELD_ICON_STROKE} aria-hidden />
);

export const PlusIcon = (): JSX.Element => (
  <Plus size={BUTTON_ICON_SIZE} strokeWidth={BUTTON_ICON_STROKE} aria-hidden />
);

export const MinusIcon = (): JSX.Element => (
  <Minus size={BUTTON_ICON_SIZE} strokeWidth={BUTTON_ICON_STROKE} aria-hidden />
);

export const CloseIcon = (): JSX.Element => <X size={BUTTON_ICON_SIZE} strokeWidth={BUTTON_ICON_STROKE} aria-hidden />;

export const FiltersIcon = (): JSX.Element => (
  <svg
    width={BUTTON_ICON_SIZE}
    height={BUTTON_ICON_SIZE}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.9}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M4 6h11M4 12h9M4 18h6" />
    <circle cx="18" cy="6" r="2" />
    <circle cx="16" cy="12" r="2" />
    <circle cx="13" cy="18" r="2" />
  </svg>
);

export const BuildingIcon = (): JSX.Element => (
  <svg
    width={16}
    height={16}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={FIELD_ICON_STROKE}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M3 21h18M6 21V7l6-4 6 4v14" />
    <path d="M10 9h4M10 13h4M10 17h4" />
  </svg>
);

export const ChevronDownIcon = (): JSX.Element => (
  <ChevronDown size={CONTROL_ICON_SIZE} strokeWidth={2.4} aria-hidden />
);

export const CalendarIcon = (): JSX.Element => <Calendar size={17} strokeWidth={FIELD_ICON_STROKE} aria-hidden />;

/** Capacity, for the resource card in the list. */
export const UsersIcon = (): JSX.Element => (
  <svg
    width={15}
    height={15}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={FIELD_ICON_STROKE}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

/** The "opens" affordance on the resource card. */
export const ChevronRightIcon = (): JSX.Element => (
  <ChevronRight size={CONTROL_ICON_SIZE + 3} strokeWidth={BUTTON_ICON_STROKE} aria-hidden />
);

/**
 * Find a coworker — the prototype's two-person glyph next to the filters (its `onOpenCw` button).
 * Both figures are needed: the single figure alone sits in the left half of the 24-box and reads
 * off-centre inside the square button.
 */
export const PersonIcon = (): JSX.Element => (
  <svg
    width={CONTROL_ICON_SIZE}
    height={CONTROL_ICON_SIZE}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.9}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

/** The search field's magnifier. */
export const SearchIcon = (): JSX.Element => (
  <svg
    width={16}
    height={16}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

export const MoreIcon = (): JSX.Element => (
  <Ellipsis size={BUTTON_ICON_SIZE} strokeWidth={BUTTON_ICON_STROKE} aria-hidden />
);

export const PencilIcon = (): JSX.Element => <Pencil size={15} strokeWidth={FIELD_ICON_STROKE} aria-hidden />;

export const TrashIcon = (): JSX.Element => <Trash2 size={15} strokeWidth={FIELD_ICON_STROKE} aria-hidden />;
